// A calendar box's settings (docs/tapestry-design.md §3.5), as its panel reads and writes them; the server reads the
// same settings (Archives.Content.Calendar) and checks them on save.
import type { CalendarShape } from "./event-time.js";

export type Month = { name: string; days: number };

/** "Hammer 30, Midwinter 1" or "12 x 30" as rows; null when it can't be read (the text is left alone then). */
export function readMonths(written: string): Month[] | null {
  const months: Month[] = [];
  for (const item of written.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean)) {
    const repeated = /^(\d{1,3})\s*[x×]\s*(\d{1,5})$/i.exec(item);
    if (repeated) {
      for (let i = 0; i < Number(repeated[1]); i++) months.push({ name: "", days: Number(repeated[2]) });
      continue;
    }
    const named = /^(.*?)\s*(\d{1,5})$/.exec(item);
    if (!named) return null;
    months.push({ name: named[1]!.trim(), days: Number(named[2]) });
  }
  return months;
}

/** Rows back into the fence's form: "Hammer 30, Midwinter 1", or "12 x 30" when all are numbered and alike. */
export function writeMonths(months: Month[]): string {
  if (months.length > 1 && months.every((m) => !m.name && m.days === months[0]!.days)) return `${months.length} x ${months[0]!.days}`;
  return months.map((m) => (m.name ? `${m.name.replace(/[,;\d"]/g, "").trim()} ${m.days}` : String(m.days))).join(", ");
}

/** The calendar a box's settings describe (null: ordinary dates), for reading dates as they're typed. */
export function shapeOf(settings: Record<string, string>): CalendarShape | null {
  const months = settings.months ? readMonths(settings.months) : null;
  if (!months || months.length === 0) return null;
  return {
    title: settings.title || "this calendar",
    era: settings.era || null,
    down: settings.count === "down",
    hours: Number(settings.hours) || 24,
    minutes: Number(settings.minutes) || 60,
    months: months.map((m) => ({ name: m.name || null, days: m.days })),
  };
}
